/**
 * Team Presence Pill — sits in the Admin Dashboard header.
 *
 * Renders two clickable sub-pills:
 *   1. "👤 4 of 5 working today" → navigates to /admin/activity#leaderboard
 *   2. "☀ first in 9:14am"       → opens a popover with per-counsellor login times
 *
 * Definitions:
 *   "Working"  = counsellor has ≥1 logged action today (green dot)
 *   "Idle"     = counsellor logged in today but 0 actions (amber dot)
 *   "Absent"   = counsellor has NOT logged in today (grey dot)
 *
 * Auto-refreshes every 60s. Admin-only (parent should gate by role).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Users } from "lucide-react";
import { api } from "@/lib/api";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

function formatTimeIST(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return null;
  }
}

function initialsOf(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Deterministic colour per name so each counsellor gets a consistent avatar shade. */
function avatarTone(name) {
  const palette = [
    "bg-rose-100 text-rose-700",
    "bg-amber-100 text-amber-800",
    "bg-emerald-100 text-emerald-700",
    "bg-sky-100 text-sky-700",
    "bg-indigo-100 text-indigo-700",
    "bg-fuchsia-100 text-fuchsia-700",
    "bg-teal-100 text-teal-700",
    "bg-orange-100 text-orange-700",
  ];
  let hash = 0;
  for (let i = 0; i < (name || "").length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return palette[hash % palette.length];
}

function statusDot(status) {
  if (status === "working") return "bg-emerald-500";
  if (status === "idle") return "bg-amber-400";
  return "bg-slate-300";
}

function statusLabel(status) {
  if (status === "working") return "working";
  if (status === "idle") return "idle";
  return "not in yet";
}

export default function TeamPresencePill() {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/admin/team-presence");
        if (!cancelled) setData(r.data);
      } catch {
        /* silent — pill is non-critical UI */
      }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (!data || data.total_counsellors === 0) return null;

  const {
    total_counsellors,
    working_today,
    first_login_at,
    breakdown = [],
  } = data;
  const ratio = working_today / Math.max(1, total_counsellors);
  const tone =
    ratio >= 0.8
      ? "text-emerald-700 bg-emerald-50 border-emerald-200"
      : ratio >= 0.5
        ? "text-amber-800 bg-amber-50 border-amber-200"
        : "text-rose-700 bg-rose-50 border-rose-200";
  const firstTime = formatTimeIST(first_login_at);

  // Earliest/latest summary for popover footer
  const withLogins = breakdown.filter((b) => b.first_login_today);
  const earliest = withLogins[0];
  const latest = withLogins[withLogins.length - 1];

  return (
    <div className="hidden items-center gap-1.5 md:flex">
      {/* Working today — navigates to leaderboard */}
      <button
        type="button"
        onClick={() => nav("/admin/activity#leaderboard")}
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-opacity hover:opacity-80 ${tone}`}
        data-testid="team-presence-working"
        title="Click to view leaderboard"
      >
        <Users className="h-3.5 w-3.5" />
        {working_today} of {total_counsellors} working today
      </button>

      {/* First in — opens popover with per-counsellor breakdown */}
      {firstTime && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600 transition-opacity hover:opacity-80"
              data-testid="team-presence-first-login"
              title="See each counsellor's check-in time"
            >
              <Sun className="h-3.5 w-3.5 text-amber-500" />
              first in {firstTime}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-80 p-0"
            data-testid="team-presence-popover"
          >
            <div className="border-b border-slate-100 px-4 py-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#0A2540]">
                <Sun className="h-4 w-4 text-amber-500" />
                Today's team check-in
              </div>
              <div className="mt-0.5 text-[11px] uppercase tracking-wider text-slate-400">
                {working_today} working · {breakdown.length} total
              </div>
            </div>

            <ol className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
              {breakdown.map((row) => {
                const t = formatTimeIST(row.first_login_today);
                return (
                  <li
                    key={row.id}
                    className="flex items-center gap-3 px-4 py-2.5"
                    data-testid={`team-presence-row-${row.id}`}
                  >
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${avatarTone(
                        row.name,
                      )}`}
                    >
                      {initialsOf(row.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`inline-block h-1.5 w-1.5 rounded-full ${statusDot(
                            row.status,
                          )}`}
                          title={statusLabel(row.status)}
                        />
                        <span className="truncate text-sm font-medium text-[#0A2540]">
                          {row.name}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {t ? (
                          <>
                            signed in {t}
                            {row.events_today > 0
                              ? ` · ${row.events_today} action${row.events_today === 1 ? "" : "s"}`
                              : " · 0 actions"}
                          </>
                        ) : (
                          "not signed in yet"
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>

            {(earliest || latest) && (
              <div className="border-t border-slate-100 bg-slate-50/50 px-4 py-2.5 text-[11px] text-slate-500">
                {earliest && (
                  <div>
                    <span className="text-slate-400">Earliest in:</span>{" "}
                    <span className="font-medium text-slate-700">
                      {earliest.name}
                    </span>{" "}
                    at {formatTimeIST(earliest.first_login_today)}
                  </div>
                )}
                {latest && latest.id !== earliest?.id && (
                  <div>
                    <span className="text-slate-400">Latest in:</span>{" "}
                    <span className="font-medium text-slate-700">
                      {latest.name}
                    </span>{" "}
                    at {formatTimeIST(latest.first_login_today)}
                  </div>
                )}
              </div>
            )}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
